import React, { useEffect, useRef, useState } from "react";
import Unit from "./Unit";
import SecondaryUnit from "./SecondaryUnit";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import { useAuth } from "../../../context/AuthProvider";
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useContextApi } from "../../../context/ContextApi";
import { AddIcon, CheckIcon, ChevronDownIcon, XIcon } from "../../../constants/Icons";

const CustomUnit = ({ onclose, unitSuggest, onAdd, selectedData, showSecondary = true, summitAuto = false }) => {
  const [suggestion, setSuggestions] = useState(unitSuggest);
  const [errors, setErrors] = useState({});
  const [AddUnitopen, setAddUnitopen] = useState(false);
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [secondaryUnitData, setSecondaryUnitData] = useState(null);
  const [conversionValue, setConversion] = useState(0);
  const [unitData, setUnitData] = useState(null);
  const conversionRef = useRef(null);
  const conversionValueRef = useRef(0);        // 🔹 always-fresh conversion value
  const addUnitOpenRef = useRef(false);         // 🔹 always-fresh modal open state
  const secondaryOpenRef = useRef(false);       // 🔹 always-fresh modal open state
  const [conversionList, setConversionList] = useState([]);
  const [showConversionList, setShowConversionList] = useState(false);
  const { checkNetwork } = useNetwork();
  const { useEscapeKey } = useContextApi();
  const { settings } = useAuth();
  const showAlert = useShowAlert();

  useEscapeKey(onclose);

  // 🔹 keep refs in sync with state
  useEffect(() => { addUnitOpenRef.current = AddUnitopen; }, [AddUnitopen]);
  useEffect(() => { secondaryOpenRef.current = secondaryOpen; }, [secondaryOpen]);

  // 🔹 always set both state and ref together
  const setConversionWithRef = (val) => {
    setConversion(val);
    conversionValueRef.current = val;
  };

  const allUnits = [
    ...(settings?.defaultUnits || []).map((u) => ({
      unitCode: u.unitCode,
      unitName: u.unitName,
    })),
    ...(settings?.storeUnits || []).map((u) => ({
      unitCode: u.unitCode,
      unitName: u.unitName,
    })),
  ];

  const primaryUnitName = allUnits.find(
    (u) => u.unitCode === selectedData?.primaryUnitCode
  )?.unitName;

  const secondaryUnitName = allUnits.find(
    (u) => u.unitCode === selectedData?.secondaryUnitCode
  )?.unitName;

  // 🔹 kept for input disabled prop only
  const isDefaultConversion = settings?.defaultUnits?.some(
    (u) =>
      u.unitCode === unitData?.primaryUnitCode &&
      u.defaultSecondaryunitCodeNormalized === secondaryUnitData?.primaryUnitCode
  );

  const handleUnit = (data) => {
    setUnitData(data);

    const defaultMatch = settings?.defaultUnits?.find(
      (u) => u?.unitCode === data?.primaryUnitCode
    );

    if (defaultMatch?.defaultSecondaryunitCodeNormalized) {
      const secondary = allUnits?.find(
        (u) => u?.unitCode === defaultMatch?.defaultSecondaryunitCodeNormalized
      );
      if (secondary) {
        setSecondaryUnitData({
          primaryUnitName: secondary?.unitName,
          primaryUnitCode: secondary?.unitCode,
        });
      }
    } else {
      setSecondaryUnitData(null);
    }

    setSuggestions((prev) => {
      const existsInStore = prev?.storeUnits?.some(
        (u) => u.unitCode === data?.primaryUnitCode
      );
      const existsInDefault = prev?.defaultUnits?.some(
        (u) => u.unitCode === data?.primaryUnitCode
      );
      if (existsInStore || existsInDefault) return prev;
      return {
        ...prev,
        storeUnits: [
          ...(prev?.storeUnits || []),
          { unitName: data?.primaryUnitName, unitCode: data?.primaryUnitCode },
        ],
      };
    });

    if (summitAuto) {
      handlePrimaryAdd(data);
    }

    // 🔹 delay so ref is still true when window keydown fires
    setTimeout(() => setAddUnitopen(false), 0);
  };

  const handleRemoveSecondary = () => {
    setSecondaryUnitData(null);
    setConversionWithRef(0); // 🔹
    setConversionList([]);
    setShowConversionList(false);
  };

  const handleSecondarUnit = (data) => {
    setSecondaryUnitData(data);
    setSuggestions((prev) => {
      const existsInStore = prev?.storeUnits?.some(
        (u) => u.unitCode === data?.primaryUnitCode
      );
      const existsInDefault = prev?.defaultUnits?.some(
        (u) => u.unitCode === data?.primaryUnitCode
      );
      if (existsInStore || existsInDefault) return prev;
      return {
        ...prev,
        storeUnits: [
          ...(prev?.storeUnits || []),
          { unitName: data?.primaryUnitName, unitCode: data?.primaryUnitCode },
        ],
      };
    });
    setSecondaryOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "conversion") {
      if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    }
    setConversionWithRef(value); // 🔹
  };

  const handleAddCustomUnit = async (formData) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.post("module/unit", formData);
      const results = response.data.results;
      setSuggestions((prev) => [...prev, ...results]);
      onAdd(results);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  // 🔹 accepts primaryCode & secondaryCode directly — no stale closure
  const handleConvertionList = async (primaryCode, secondaryCode) => {
    try {
      if (!checkNetwork()) return;

      const response = await AxiosInstance.get(
        `module/unit/conversions?primaryUnitCode=${primaryCode}&secondaryUnitCode=${secondaryCode}`
      );
      const results = response.data.results;

      // 🔹 compute isDefault fresh inside async function
      const isDefault = settings?.defaultUnits?.some(
        (u) =>
          u.unitCode === primaryCode &&
          u.defaultSecondaryunitCodeNormalized === secondaryCode
      );

      if (isDefault) {
        setConversionWithRef(results[0]?.conversion || 0); // 🔹
      } else if (selectedData?.conversion) {
        const match = results.find((it) => selectedData?.conversion === it?.conversion);
        setConversionWithRef(match?.conversion || 0); // 🔹
      } else {
        setConversionWithRef(0); // 🔹
      }

      setConversionList(results);
      setShowConversionList(true);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleAddCustom = () => {
    const newErrors = {};

    const primaryName = unitData.primaryUnitName.trim();
    const primaryCode = unitData.primaryUnitCode.trim();
    const secondaryName = secondaryUnitData.primaryUnitName.trim();
    const secondaryCode = secondaryUnitData.primaryUnitCode.trim();
    const conversion = conversionValueRef.current; // 🔹 always fresh, no stale closure

    if (!primaryName) newErrors.primaryUnitName = "Primary Unit Name is required";
    if (primaryName && !primaryCode) newErrors.primaryUnitCode = "Primary Code is required";
    if (secondaryName) {
      if (!secondaryCode) newErrors.secondaryUnitCode = "Secondary Code is required";
      if (!conversion) newErrors.conversion = "Conversion value is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const payload = { primaryUnitName: primaryName, primaryUnitCode: primaryCode };
    if (secondaryName) {
      payload.secondaryUnitName = secondaryName;
      payload.secondaryUnitCode = secondaryCode;
      payload.conversion = Number(conversion); // 🔹 correct value
    }

    handleAddCustomUnit(payload);
    setUnitData(null);
    setSecondaryUnitData(null);
    setConversionWithRef(0); // 🔹
    onclose();
  };

  const handlePrimaryAdd = (unitDataParam) => {
    const newErrors = {};

    const resolvedData =
      unitDataParam && !(unitDataParam instanceof Event) && !unitDataParam?._reactName
        ? unitDataParam
        : unitData;

    if (!resolvedData || !resolvedData?.primaryUnitName?.trim()) {
      newErrors.primaryUnitName = "Primary Unit is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const payload = {
      primaryUnitName: resolvedData?.primaryUnitName.trim(),
      primaryUnitCode: resolvedData?.primaryUnitCode.trim(),
    };
    onAdd(payload);
    setUnitData(null);
    setSecondaryUnitData(null);
    setConversionWithRef(0); // 🔹
    onclose();
  };

  const handleEnterkey = (e) => {
    if (e.key === "Enter") {
      if (unitData && secondaryUnitData) {
        handleAddCustom();
      } else {
        handlePrimaryAdd();
      }
    }
  };

  // 🔹 window keydown — uses refs so never stale
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        if (addUnitOpenRef.current || secondaryOpenRef.current) return; // 🔹 guard via ref
        if (document.activeElement === conversionRef.current) return;

        if (unitData && secondaryUnitData) {
          handleAddCustom();
        } else {
          handlePrimaryAdd();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [unitData, secondaryUnitData]);

  useEffect(() => {
    setSuggestions(unitSuggest);
    if (!selectedData) {
      setAddUnitopen(true);
    }
  }, []);

  useEffect(() => {
    if (selectedData) {
      setUnitData({
        primaryUnitName: primaryUnitName,
        primaryUnitCode: selectedData?.primaryUnitCode,
      });

      if (secondaryUnitName) {
        setSecondaryUnitData({
          primaryUnitName: secondaryUnitName,
          primaryUnitCode: selectedData?.secondaryUnitCode,
        });
      }

      if (selectedData?.conversion) {
        setConversionWithRef(selectedData?.conversion); // 🔹
      }
    }
  }, [selectedData]);

  // 🔹 pass codes directly to avoid stale closure in handleConvertionList
  useEffect(() => {
    if (unitData && secondaryUnitData) {
      handleConvertionList(
        unitData.primaryUnitCode,
        secondaryUnitData.primaryUnitCode
      );
    }
  }, [unitData, secondaryUnitData]);

  return (
    <div className="modal-overlay">
      <div className="modal-backdrop show"></div>
      <div className="modal show" style={{ display: "block" }} role="dialog" aria-modal="true">
        <div className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${unitData && secondaryUnitData ? "modal-mdc" : ""}`}>
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Unit</h6>
              <button className="btn-close" onClick={onclose}></button>
            </div>

            <div className="modal-body bg-light">
              <div className="row g-3 align-items-start">
                <div className={`${unitData && secondaryUnitData && showSecondary ? "col-6" : "col-12"}`}>
                  <div className="card mb-0">
                    <div className="card-body">
                      <label htmlFor="unit" className="form-label">
                        Primary Unit <span className="text-danger">*</span>
                      </label>
                      <div className="suggestions-unitlist mb-2" onClick={() => setAddUnitopen(true)}>
                        <div className="suggestion-item highlighted d-flex justify-content-between">
                          {unitData ? (
                            <div style={{ fontWeight: 600 }}>
                              {unitData?.primaryUnitName}
                              <small className="ms-2">({unitData?.primaryUnitCode})</small>
                            </div>
                          ) : (
                            <div className="d-flex gap-2">
                              <AddIcon iClass={"fs-15"}/> Add Unit
                            </div>
                          )}
                          {unitData && <div><ChevronDownIcon iClass={"fs-15"}/></div>}
                        </div>
                        {errors.primaryUnitName && (
                          <div className="invalid-feedback">{errors.primaryUnitName}</div>
                        )}
                      </div>

                      {unitData && showSecondary && (
                        <>
                          <label htmlFor="unit" className="form-label fs-13">
                            Secondary Unit (Optional)
                          </label>
                          <div className="suggestions-unitlist text-muted" onClick={() => setSecondaryOpen(true)}>
                            <div className="suggestion-item highlighted d-flex justify-content-between unit-item">
                              {secondaryUnitData ? (
                                <div className="fw-600 fs-13">
                                  {secondaryUnitData?.primaryUnitName}
                                  <small className="text-muted ms-2">({secondaryUnitData?.primaryUnitCode})</small>
                                </div>
                              ) : (
                                <div className="d-flex gap-2 fs-13">
                                  <AddIcon/> Add Unit
                                </div>
                              )}
                              <div className="d-flex align-items-center gap-2 fs-13">
                                {secondaryUnitData && (
                                  <>
                                    <CancelRoundedIcon
                                      className="close-icon"
                                      sx={{ fontSize: 13 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveSecondary();
                                      }}
                                    />
                                    <ChevronDownIcon iClass={"fs-13"}/>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {unitData && secondaryUnitData && showSecondary && (
                  <div className="col-6">
                    <div className="card mb-0">
                      <div className="card-body">
                        <div className="form-label p-1 btn-unit">Conversion</div>
                        <div className="d-flex align-items-center gap-3 mb-2">
                          <span>1 {unitData.primaryUnitCode}</span>
                          <span>=</span>
                          <input
                            type="text"
                            className="form-control w-30"
                            name="conversion"
                            ref={conversionRef}
                            value={conversionValue}
                            onChange={handleChange}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={handleEnterkey}
                            disabled={isDefaultConversion}
                            autoFocus
                          />
                          <span>{secondaryUnitData.primaryUnitCode}</span>
                        </div>
                        {errors.conversion && (
                          <div className="invalid-feedback">{errors.conversion}</div>
                        )}
                      </div>
                    </div>

                    {showConversionList && conversionList?.length > 1 && (
                      <div className="card mt-2">
                        <div className="card-body">
                          <div className="form-label p-1 btn-unit">Choose Value</div>
                          <ul className="suggestions-unitlist" style={{ maxHeight: "200px", overflowY: "auto" }}>
                            {conversionList?.map((item) => (
                              <li
                                key={item?._id}
                                className={`suggestion-item border-none d-flex justify-content-between ${
                                  item.conversion === conversionValue ? "active-suggestion" : ""
                                }`}
                                onClick={() => {
                                  setConversionWithRef(item?.conversion); // 🔹
                                  conversionRef.current.focus();
                                }}
                              >
                                <span>
                                  1 {unitData?.primaryUnitCode} = {item?.conversion} {secondaryUnitData?.primaryUnitCode}
                                </span>
                                {item.conversion === conversionValue && (
                                  <span><CheckIcon iClass={"fs-15"}/></span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer d-block">
              <button type="button" className="btn btn-outline-secondary text-left" onClick={onclose}>
                <XIcon/> Close
              </button>
              <div className="btn-group text-right align-items-center">
                <button
                  onClick={unitData && secondaryUnitData ? handleAddCustom : handlePrimaryAdd}
                  className="btn btn-unit text-right"
                >
                  <CheckIcon iClass={"fs-15"}/> Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {AddUnitopen && (
        <Unit
          onClose={() => {
            setAddUnitopen(false);
            onclose();
          }}
          unitSuggest={suggestion}
          onAdd={handleUnit}
          secondaryUnitData={secondaryUnitData}
        />
      )}
      {secondaryOpen && (
        <SecondaryUnit
          onClose={() => setSecondaryOpen(false)}
          unitSuggest={suggestion}
          onAdd={handleSecondarUnit}
          unitData={unitData}
        />
      )}
    </div>
  );
};

export default CustomUnit;